// Components
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingWrapper from 'components/LoadingWrapper';
// Types
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
// Utils
import { getText } from 'i18n/intl';
// Slices
import {
  createHolidayReservation,
  fetchHolidayRegions,
  getListOfHolidays,
  holidayRegionsSelector,
} from 'slices/holidayReservation.slice';
import { useAppDispatch } from 'slices/store';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import type { THolidayRegion, THolidayType } from '@timeedit/types/lib/types';
import { Button, DefaultContentLayout, TEInput } from '@timeedit/ui-components';
import { DatePicker, Modal, Typography } from '@timeedit/ui-components/antd';
// Styles
import './index.scss';

dayjs.extend(utc);

const HolidayReservationsPage = () => {
  const [region, setRegion] = useState<string | undefined>(undefined);
  const [holidayType, setHolidayType] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<dayjs.Dayjs>(
    dayjs().utc().startOf('year')
  );
  const [toDate, setToDate] = useState<dayjs.Dayjs>(
    dayjs().utc().endOf('year')
  );
  const [loading, setLoading] = useState<boolean>(false);
  const regions: THolidayRegion[] = useSelector(holidayRegionsSelector);

  const [modal, contextHolder] = Modal.useModal();

  const regionOptions = useMemo(() => {
    return regions.map((region) => ({
      value: region.id,
      label: region.name,
    }));
  }, [regions]);

  const holidayTypeOptions = useMemo(() => {
    const holidayTypeByRegionId: Record<string, THolidayType[]> =
      regions.reduce(
        (acc, region) => ({
          ...acc,
          [region.id]: region.holidayTypes,
        }),
        {}
      );
    return region
      ? holidayTypeByRegionId[region].map((holidayType: THolidayType) => ({
          value: holidayType.id,
          label: holidayType.name,
        }))
      : [];
  }, [regions, region]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchHolidayRegions());
  }, []);

  const createHolidays = async () => {
    try {
      setLoading(true);
      const { numberOfHolidaysCreated } = await createHolidayReservation({
        holidayRegion: region!,
        holidayType: holidayType!,
        startDate: fromDate.unix(),
        endDate: toDate.endOf('day').unix(),
      });
      message.success(
        `Successfully created ${numberOfHolidaysCreated} holiday reservations`
      );
    } catch (e: any) {
      errorNotification(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async () => {
    try {
      setLoading(true);
      const holidays = await getListOfHolidays(
        region!,
        holidayType!,
        fromDate.unix(),
        toDate.endOf('day').unix()
      );

      modal.confirm({
        title: 'Create holiday reservations',
        width: 600,
        content: (
          <div>
            <p>{`There are ${holidays.length} holidays in the interval:`}</p>
            <br />
            {holidays.map((holiday) => {
              const startTime = dayjs.unix(holiday.startTime).utc();
              const endTime = dayjs.unix(holiday.endTime).utc();

              return (
                <div key={holiday.name}>
                  {startTime.format('YYYY-MM-DD')} <b>{holiday.name}</b>{' '}
                  {endTime.diff(startTime, 'hour') > 12
                    ? ''
                    : `(${startTime.format('HH:mm')} - ${endTime.format(
                        'HH:mm'
                      )})`}
                </div>
              );
            })}
          </div>
        ),
        onOk: createHolidays,
        okText: 'Create',
      });
    } catch (e: any) {
      errorNotification(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultContentLayout
      title={getText('holiday_reservations_title')}
      subtitle={getText('holiday_reservations_subtitle')}
    >
      <LoadingWrapper isLoading={false}>
        <div className="holiday-reservations">
          <div className="content">
            <Typography.Title level={4}>General</Typography.Title>
            <TEInput.Select
              label="Select country"
              tooltip="Select country"
              options={regionOptions}
              value={region}
              onChange={(val) => {
                setRegion(val);
                setHolidayType(undefined);
              }}
            />
            <TEInput.Select
              label="Select holiday type"
              tooltip="Select holiday type"
              options={holidayTypeOptions}
              value={holidayType}
              onChange={setHolidayType}
            />
            <TEInput.LabelWrapper label="From" tooltip="From">
              <DatePicker
                size="small"
                value={fromDate}
                onChange={(val) => setFromDate(val ?? undefined)}
              />
            </TEInput.LabelWrapper>
            <TEInput.LabelWrapper label="To" tooltip="To">
              <DatePicker
                size="small"
                value={toDate}
                onChange={(val) => setToDate(val ?? undefined)}
              />
            </TEInput.LabelWrapper>
            <TEInput.LabelWrapper
              label="Create holiday reservations"
              tooltip="Create holiday reservations"
            >
              <Button
                type="primary"
                size="small"
                disabled={!region || !holidayType}
                onClick={onCreate}
                loading={loading}
              >
                Create
              </Button>
            </TEInput.LabelWrapper>
          </div>
          {contextHolder}
        </div>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default HolidayReservationsPage;
